import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useMyCards, useBlockCard, useUnblockCard } from '../../hooks/useCards';
import type { CardStatus, CardType, CardBrand } from '../../types/cards';

const statusColors: Record<CardStatus, 'success' | 'primary' | 'default' | 'error'> = {
  PENDING_ACTIVATION: 'primary',
  ACTIVE: 'success',
  BLOCKED: 'error',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

const statusLabels: Record<CardStatus, string> = {
  PENDING_ACTIVATION: 'Pendente',
  ACTIVE: 'Ativo',
  BLOCKED: 'Bloqueado',
  CANCELLED: 'Cancelado',
  EXPIRED: 'Expirado',
};

const typeLabels: Record<CardType, string> = {
  PREPAID: 'Pré-pago',
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
};

const brandColors: Record<CardBrand, string> = {
  VISA: 'bg-blue-600',
  MASTERCARD: 'bg-red-600',
  ELO: 'bg-yellow-500',
};

export function MyCards() {
  const navigate = useNavigate();
  const { data: cards, isLoading } = useMyCards();
  const blockCard = useBlockCard();
  const unblockCard = useUnblockCard();

  const handleBlock = async (cardId: string) => {
    const reason = prompt('Motivo do bloqueio:');
    if (reason) {
      await blockCard.mutateAsync({ cardId, reason });
    }
  };

  const handleUnblock = async (cardId: string) => {
    await unblockCard.mutateAsync(cardId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meus Cartões</h1>
          <p className="text-gray-500">Gerencie seus cartões virtuais e físicos</p>
        </div>
        <Button onClick={() => navigate('/banking/cards/request')}>
          Solicitar Cartão
        </Button>
      </div>

      {cards && cards.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2">
          {cards.map((card) => (
            <Card key={card.id} className="overflow-hidden">
              {/* Card Visual */}
              <div className={`${brandColors[card.brand]} text-white p-6 relative`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm opacity-80">{typeLabels[card.type]}</p>
                    <p className="text-lg font-mono mt-4">{card.panMasked}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{card.brand}</p>
                    {card.isPhysical && (
                      <Badge variant="default" className="mt-2 bg-white/20">
                        Físico
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-end">
                  <div>
                    <p className="text-xs opacity-80">Titular</p>
                    <p className="font-medium">{card.cardholderName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs opacity-80">Validade</p>
                    <p className="font-medium">
                      {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                    </p>
                  </div>
                </div>
              </div>

              {/* Card Info */}
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Badge variant={statusColors[card.status]}>
                    {statusLabels[card.status]}
                  </Badge>
                  <div className="flex gap-2">
                    {card.onlinePurchasesEnabled && (
                      <Badge variant="default">Online</Badge>
                    )}
                    {card.internationalEnabled && (
                      <Badge variant="default">Internacional</Badge>
                    )}
                    {card.contactlessEnabled && (
                      <Badge variant="default">Contactless</Badge>
                    )}
                  </div>
                </div>

                {card.type === 'CREDIT' ? (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Limite Total</span>
                      <span className="font-medium">{formatCurrency(card.creditLimit || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Disponível</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(card.availableLimit || 0)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{
                          width: `${((card.availableLimit || 0) / (card.creditLimit || 1)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Limite Diário</span>
                      <span className="font-medium">{formatCurrency(card.dailyLimit)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Limite por Transação</span>
                      <span className="font-medium">{formatCurrency(card.transactionLimit)}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => navigate(`/banking/cards/${card.id}`)}
                  >
                    Detalhes
                  </Button>
                  {card.status === 'ACTIVE' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleBlock(card.id)}
                      disabled={blockCard.isPending}
                    >
                      Bloquear
                    </Button>
                  )}
                  {card.status === 'BLOCKED' && (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleUnblock(card.id)}
                      disabled={unblockCard.isPending}
                    >
                      Desbloquear
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">Você ainda não possui cartões</p>
            <Button onClick={() => navigate('/banking/cards/request')}>
              Solicitar Primeiro Cartão
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default MyCards;
